type OpenGraphImage =
  | string
  | {
      url: string;
      width?: number;
      height?: number;
      alt?: string;
    };

type RobotsConfig = {
  index?: boolean;
  follow?: boolean;
  googleBot?: {
    index?: boolean;
    follow?: boolean;
  };
};

export type PageMetadata = {
  metadataBase?: string;
  title?: string;
  description?: string;
  icons?: Array<{ rel: string; url: string }>;
  openGraph?: {
    images?: OpenGraphImage | OpenGraphImage[];
  };
  alternates?: {
    canonical?: string;
  };
  robots?: RobotsConfig;
};

export function metadataToHead(metadata: PageMetadata) {
  const baseUrl = metadata.metadataBase ? new URL(metadata.metadataBase) : undefined;

  const meta: Array<Record<string, string>> = [];
  const links: Array<Record<string, string>> = [];

  if (metadata.description) {
    meta.push({ name: "description", content: metadata.description });
    meta.push({ property: "og:description", content: metadata.description });
  }

  if (metadata.title) {
    meta.push({ property: "og:title", content: metadata.title });
  }

  if (metadata.alternates?.canonical) {
    const canonicalUrl = resolveUrl(metadata.alternates.canonical, baseUrl);
    links.push({ rel: "canonical", href: canonicalUrl });
    meta.push({ property: "og:url", content: canonicalUrl });
  }

  if (metadata.robots) {
    const robots = buildRobotsContent(metadata.robots);
    if (robots) {
      meta.push({ name: "robots", content: robots });
    }

    const googleBot = buildRobotsContent(metadata.robots.googleBot);
    if (googleBot) {
      meta.push({ name: "googlebot", content: googleBot });
    }
  }

  for (const icon of metadata.icons ?? []) {
    links.push({
      rel: icon.rel,
      href: resolveUrl(icon.url, baseUrl),
    });
  }

  const images = normalizeImages(metadata.openGraph?.images);
  for (const image of images) {
    const url = resolveUrl(image.url, baseUrl);
    meta.push({ property: "og:image", content: url });

    if (image.width) {
      meta.push({ property: "og:image:width", content: `${image.width}` });
    }

    if (image.height) {
      meta.push({ property: "og:image:height", content: `${image.height}` });
    }

    if (image.alt) {
      meta.push({ property: "og:image:alt", content: image.alt });
    }
  }

  return {
    title: metadata.title,
    meta,
    links,
  };
}

function buildRobotsContent(robots?: { index?: boolean; follow?: boolean }) {
  if (!robots) {
    return undefined;
  }

  const directives: string[] = [];

  if (robots.index !== undefined) {
    directives.push(robots.index ? "index" : "noindex");
  }

  if (robots.follow !== undefined) {
    directives.push(robots.follow ? "follow" : "nofollow");
  }

  return directives.length ? directives.join(", ") : undefined;
}

function normalizeImages(images?: OpenGraphImage | OpenGraphImage[]) {
  if (!images) {
    return [];
  }

  return (Array.isArray(images) ? images : [images]).map((image) =>
    typeof image === "string" ? { url: image } : image,
  );
}

function resolveUrl(input: string, baseUrl?: URL) {
  if (!baseUrl) {
    return input;
  }

  return new URL(input, baseUrl).toString();
}
